/*
 * PlanCompare — monthly render allowance by plan.
 *
 * Values come from backend/plan_gates.py, not the marketing page:
 *   free 3 · creator 60 · pro 150 · agency 500 renders per month.
 *
 * Encoding is bar length on one shared linear scale. The scale is
 * deliberately NOT log or broken: Free really is 3 of 500, and
 * compressing that would misrepresent the offer. Because a 3-unit
 * bar is nearly invisible, every value is also printed as text and
 * the whole graphic is exposed as a table to assistive technology.
 */

const PLANS = [
  { name: "Free",    price: "$0",    renders: 3 },
  { name: "Creator", price: "$19",   renders: 60 },
  { name: "Pro",     price: "$49",   renders: 150, feature: true },
  { name: "Agency",  price: "$149",  renders: 500 },
];

const MAX = 500;

export default function PlanCompare() {
  return (
    <figure className="m-0 border border-rule bg-canvas">
      <figcaption className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-4 border-b border-rule">
        <span className="eyebrow">Renders per month</span>
        <span className="font-mono text-xs text-ink-subtle">
          one bar unit = one render · scale 0–{MAX}
        </span>
      </figcaption>

      <table className="w-full border-collapse">
        <caption className="sr-only">
          Monthly render allowance by DemoCraft plan. Free: 3 renders at $0.
          Creator: 60 renders at $19. Pro: 150 renders at $49. Agency: 500
          renders at $149.
        </caption>
        <thead className="sr-only">
          <tr><th scope="col">Plan</th><th scope="col">Price per month</th><th scope="col">Renders per month</th></tr>
        </thead>
        <tbody>
          {PLANS.map((p) => (
            <tr key={p.name} className="border-b border-rule last:border-b-0">
              <th scope="row" className="text-left align-middle p-3 sm:p-5 pr-2 sm:pr-3 w-px whitespace-nowrap">
                <span className="font-display font-black text-lg tracking-tight">{p.name}</span>
                {/* Widest element in the row; below `sm` it is what pushed
                    the table past the viewport, so it drops out there. */}
                {p.feature && <span className="brutal-chip brutal-chip-accent ml-2 align-middle hidden sm:inline-flex">Popular</span>}
              </th>
              <td className="align-middle p-3 sm:p-5 px-2 sm:px-3 w-px font-mono text-sm text-ink-muted tabular-nums whitespace-nowrap">
                {p.price}<span className="text-ink-subtle hidden sm:inline">/mo</span>
              </td>
              <td className="align-middle p-3 sm:p-5 pl-2 sm:pl-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* No minimum-width floor on the scale: Free really is
                      3 of 500 and padding the bar would overstate it. The
                      2px is a rendering floor so a hairline stays visible;
                      the exact count is printed beside every bar. */}
                  <div className="flex-1 h-4 sm:h-5 bg-surface border border-rule min-w-0">
                    <div
                      className="h-full bg-brand border-r border-rule"
                      style={{ width: `${(p.renders / MAX) * 100}%`, minWidth: "2px" }}
                    />
                  </div>
                  <span className="font-mono text-sm font-bold tabular-nums w-9 sm:w-12 text-right shrink-0">
                    {p.renders}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
